import Vue from 'vue';
import store from '@/store';
import { i18nStoreTypes, enrolmentStoreTypes, applicationStoreTypes } from '@/store/types';
import { memberRegistrationEndpointsTypes } from '@/http/endpoints/MemberRegistrationEndpoints';
import { clientEndpointsTypes } from '@/http/endpoints/ClientEndpoints';

const URLS = store.getters[applicationStoreTypes.getters.URLS];
const isBloom = store.getters[applicationStoreTypes.getters.IS_BLOOM];

function createMapper() {
  function getDefaultValues() {
    return {
      serviceType: '',
      member: {
        firstName: '',
        lastName: '',
        email: '',
        birthdate: '',
        geoStateAcronym: '',
        zipCode: '',
      },
      guardian: {
        firstName: '',
        lastName: '',
        email: '',
      },
      healthInsurance: {
        payerKey: '',
        memberId: '',
      },
      subscriber: {
        coverageType: '',
        firstName: '',
        lastName: '',
        birthdate: '',
        relationship: '',
      },
      legal: {
        termsAccepted: '',
        hipaaAccepted: '',
        minorConsentAccepted: '',
        gdprAccepted: '',
        gorillaAccepted: '',
      },
      bloom: {
        vaginalAnatomy: '',
      },
    };
  }

  function parseFromAPI(apiData) {
    if (!apiData) return null;

    const defaultValues = getDefaultValues();
    const formData = getDefaultValues();

    formData.serviceType = apiData.service_type ?? defaultValues.serviceType;

    formData.member = {
      firstName: apiData.first_name ?? defaultValues.member.firstName,
      lastName: apiData.last_name ?? defaultValues.member.lastName,
      email: apiData.email ?? defaultValues.member.email,
      birthdate: apiData.birthdate ?? defaultValues.member.birthdate,
      geoStateAcronym: apiData.geographical_state ?? defaultValues.member.geoStateAcronym,
      zipCode: apiData.zip_code ?? defaultValues.member.zipCode,
    };

    formData.healthInsurance = {
      payerKey: apiData.payer?.key ?? defaultValues.healthInsurance.payerKey,
      memberId: apiData.identification ?? defaultValues.healthInsurance.memberId,
    };

    if (apiData.guardian) {
      formData.guardian = {
        firstName: apiData.guardian.first_name ?? defaultValues.guardian.firstName,
        lastName: apiData.guardian.last_name ?? defaultValues.guardian.lastName,
        email: apiData.guardian.email ?? defaultValues.guardian.email,
      };
    }

    if (apiData.subscriber) {
      formData.subscriber = {
        coverageType: apiData.coverage_type ?? defaultValues.subscriber.coverageType,
        firstName: apiData.subscriber.first_name ?? defaultValues.subscriber.firstName,
        lastName: apiData.subscriber.last_name ?? defaultValues.subscriber.lastName,
        birthdate: apiData.subscriber.birthdate ?? defaultValues.subscriber.birthdate,
        relationship: apiData.subscriber.relationship ?? defaultValues.subscriber.relationship,
      };
    }

    if (apiData.legal) {
      formData.legal = {
        termsAccepted: apiData.legal.accepts_terms || defaultValues.legal.termsAccepted,
        hipaaAccepted: apiData.legal.hipaa_consent || defaultValues.legal.hipaaAccepted,
        gdprAccepted: apiData.legal.gdpr_consent || defaultValues.legal.gdprAccepted,
        minorConsentAccepted: Boolean(apiData.legal.minor_consent_accepted_url) || defaultValues.legal.minorConsentAccepted,
        gorillaAccepted: apiData.legal.health_gorilla_consent || defaultValues.legal.gorillaAccepted,
      };
    }

    if (apiData.vaginal_anatomy) {
      formData.bloom = {
        vaginalAnatomy: apiData.vaginal_anatomy ?? defaultValues.bloom.vaginalAnatomy,
      };
    }

    return formData;
  }

  function parseToAPI(formData, payers, clientLegalLinks, language, programType, serviceType, utmParams) {
    const {
      member, guardian, healthInsurance, subscriber, legal, bloom, saml, amwell,
    } = formData;

    const hasGuardian = !!guardian.firstName;
    const hasSubscriber = !!subscriber.firstName;
    const hasBloom = !!bloom.vaginalAnatomy;
    const { payerKey, memberId } = healthInsurance;
    const { coverageType } = subscriber;

    const apiData = {
      first_name: member.firstName,
      last_name: member.lastName,
      email: member.email,
      birthdate: member.birthdate,
      zip_code: member.zipCode,
      legal: {
        accepts_terms: Boolean(legal.termsAccepted),
        hipaa_consent: Boolean(legal.hipaaAccepted),
        gdpr_consent: Boolean(legal.gdprAccepted),
        health_gorilla_consent: Boolean(legal.gorillaAccepted),
        health_gorilla_consent_url: clientLegalLinks.gorillaConsent || undefined,
        hipaa_consent_url: clientLegalLinks.hipaaConsent || undefined,
        terms_and_conditions_url: clientLegalLinks.termsAndConditions || undefined,
        privacy_statement_url: clientLegalLinks.privacyStatement || undefined,
        telehealth_consent_url: clientLegalLinks.consentTelehealthServices || undefined,
        non_discrimination_url: clientLegalLinks.nonDiscrimination || undefined,
        minor_consent_accepted_url: URLS.MINOR_ELIGIBILITY_CONSENT_URL || undefined,
        gdpr_consent_url: clientLegalLinks.gdprConsent || undefined,
      },
      tracking: utmParams,
      saml,
      amwell,
    };

    if (member.geoStateAcronym) {
      apiData.geographical_state = member.geoStateAcronym;
    }

    if (hasGuardian) {
      apiData.guardian = {
        first_name: guardian.firstName,
        last_name: guardian.lastName,
        email: guardian.email,
      };
    }

    if (payerKey) {
      const selectedPayer = payers.find(payer => payer.key === payerKey);
      apiData.payer = {
        key: selectedPayer.key,
        name: selectedPayer.name,
      };
    }

    if (coverageType) {
      apiData.coverage_type = coverageType;
    }

    if (memberId) {
      apiData.identification = memberId;
    }

    if (hasSubscriber) {
      apiData.subscriber = {
        first_name: subscriber.firstName,
        last_name: subscriber.lastName,
        birthdate: subscriber.birthdate,
        relationship: subscriber.relationship || undefined,
      };
    }

    if (language) {
      apiData.language = language;
    }

    if (programType) {
      apiData.program_type = programType.toUpperCase();
    }

    if (serviceType) {
      apiData.service_type = serviceType;
    }

    if (hasBloom) {
      apiData.bloom = {
        vaginal_anatomy: bloom.vaginalAnatomy,
      };
    }

    return apiData;
  }

  return { getDefaultValues, parseFromAPI, parseToAPI };
}

function createConstants() {
  const SAVE_ELIGIBILITY_ERROR_TITLE = 'onboarding_message_thank_you';
  const NOT_ELIGIBLE_ERROR_TITLE = 'onboarding_eligibility_failed2_title';
  const SAVE_ELIGIBILITY_ERRORS = ({ clientName }) => ({
    DEFAULT: {
      title: SAVE_ELIGIBILITY_ERROR_TITLE,
      text: 'onboarding_message_availity_label',
    },
    CAP_LIMIT_REACHED: {
      title: 'onboarding2_title_message_unable_to_enroll',
      text: ['onboarding_message_cap_limit', { clientName }],
    },
    MINOR_ENROLLMENTS_NOT_ALLOWED: {
      title: SAVE_ELIGIBILITY_ERROR_TITLE,
      text: 'onboarding_minor_enrollment_block_feedback_message',
    },
    UNDERAGE_ENROLLMENTS_NOT_ALLOWED: {
      title: SAVE_ELIGIBILITY_ERROR_TITLE,
      text: 'onboarding2_age_guard_13',
    },
    MEMBER_AGE_GUARD_13: {
      title: SAVE_ELIGIBILITY_ERROR_TITLE,
      text: 'onboarding_minor_enrollment_block_feedback_message',
    },
    ENROLLMENT_NOT_FOUND: {
      title: SAVE_ELIGIBILITY_ERROR_TITLE,
      text: 'onboarding2_subtitle_unable_to_recover',
    },
    // Bloom :: Vaginal Anatomy question validation
    BLOOM_NO_VAGINAL_ANATOMY: {
      title: 'onboardingbloom_message_thank_you',
      text: 'onboardingblooom_message_unfortunately_vaginal_anatomy',
    },
  });
  const NOT_ELIGIBLE_ERRORS = ({ clientName }) => ({
    default: {
      title: NOT_ELIGIBLE_ERROR_TITLE,
      text: 'onboarding_eligibility_failed2_body',
    },
    not_eligible: {
      title: NOT_ELIGIBLE_ERROR_TITLE,
      text: 'onboarding_eligibility_failed2_body',
    },
    no_slots_available: {
      title: 'onboarding2_title_message_unable_to_enroll',
      text: ['onboarding_message_cap_limit', { clientName }],
    },
    cap_limit_reached: {
      title: 'onboarding2_title_message_unable_to_enroll',
      text: ['onboarding_message_cap_limit', { clientName }],
    },
  });

  return { SAVE_ELIGIBILITY_ERRORS, NOT_ELIGIBLE_ERRORS };
}

const parseSubmitResponse = response => ({
  eligible: response?.is_eligible,
  excluded_reason: response?.excluded_reason,
  client_reference: response?.client_reference,
});

const mapper = createMapper();
export const ELIGIBILITY_CONSTANTS = createConstants();

class EligibilityService {
  constructor() {
    this.serviceLoaded = false;
    this.payerList = [];
    this.sso = [];

    const defaultValues = mapper.getDefaultValues();
    this.member = defaultValues.member;
    this.guardian = defaultValues.guardian;
    this.coverageType = defaultValues.coverageType;
    this.healthInsurance = defaultValues.healthInsurance;
    this.subscriber = defaultValues.subscriber;
    this.legal = defaultValues.legal;

    this.version = undefined;
    this.canSubmit = undefined;

    if (isBloom) {
      this.bloom = defaultValues.bloom;
    }
  }

  isMemberEmpty() {
    return !this.member.firstName;
  }

  getServiceData() {
    const data = {
      member: { ...this.member },
      guardian: { ...this.guardian },
      coverageType: { ...this.coverageType },
      healthInsurance: { ...this.healthInsurance },
      subscriber: { ...this.subscriber },
      legal: { ...this.legal },
      bloom: { ...this.bloom },
    };

    if (this.samlV2?.enable_integration) {
      data.saml = {
        enable_integration: this.samlV2.enable_integration,
        user_uuid: this.samlV2.user_uuid,
        integration_identifier: this.samlV2.integration_identifier,
        attributes: this.samlV2.attributes,
      };
    }

    if (this.amwell?.enable_integration) {
      data.amwell = {
        enable_integration: this.amwell.enable_integration,
        user_uuid: this.amwell.user_uuid,
      };
    }

    return data;
  }

  setServiceData(eligibilityData) {
    this.serviceType = eligibilityData.serviceType;
    this.member = { ...eligibilityData.member };
    this.guardian = { ...eligibilityData.guardian };
    this.coverageType = { ...eligibilityData.coverageType };
    this.healthInsurance = { ...eligibilityData.healthInsurance };
    this.subscriber = { ...eligibilityData.subscriber };
    this.legal = { ...eligibilityData.legal };
    this.bloom = { ...eligibilityData.bloom };
  }

  setSamlV2ServiceData(data) {
    const defaultValues = mapper.getDefaultValues();

    this.samlV2 = {
      enable_integration: true,
      user_uuid: data.user_uuid,
      integration_identifier: data.integration_identifier,
      attributes: data.saml_attributes,
    };

    const coreAttrs = data.saml_attributes?.core;
    this.member = {
      firstName: coreAttrs?.first_name ?? defaultValues.member.firstName,
      lastName: coreAttrs?.last_name ?? defaultValues.member.lastName,
      email: coreAttrs?.email ?? defaultValues.member.email,
      birthdate: coreAttrs?.date_of_birth ?? defaultValues.member.birthdate,
      geoStateAcronym: coreAttrs?.state ?? defaultValues.member.geoStateAcronym,
      zipCode: coreAttrs?.zip_code ?? defaultValues.member.zipCode,
    };
    this.member.spec = {
      firstName: {
        readOnly: !!this.member.firstName,
      },
      lastName: {
        readOnly: !!this.member.lastName,
      },
      email: {
        readOnly: !!this.member.email,
      },
      birthdate: {
        readOnly: !!this.member.birthdate,
      },
      geoStateAcronym: {
        readOnly: !!this.member.geoStateAcronym,
      },
      zipCode: {
        readOnly: !!this.member.zipCode,
      },
    };

    this.guardian = defaultValues.guardian;
    this.coverageType = defaultValues.coverageType;
    this.healthInsurance = defaultValues.healthInsurance;
    this.subscriber = defaultValues.subscriber;
    this.legal = defaultValues.legal;
    this.bloom = defaultValues.bloom;
  }

  setAmwellServiceData(data, geoStateOptions) {
    const defaultValues = mapper.getDefaultValues();

    this.amwell = {
      enable_integration: true,
      user_uuid: data.user_uid,
    };

    this.member = {
      firstName: data.first_name ?? defaultValues.member.firstName,
      lastName: data.last_name ?? defaultValues.member.lastName,
      email: data.email ?? defaultValues.member.email,
      birthdate: data.birth_date ?? defaultValues.member.birthdate,
      geoStateAcronym: data.state ?? defaultValues.member.geoStateAcronym,
      zipCode: data.zip_code ?? defaultValues.member.zipCode,
    };

    const enableGeoState = () => {
      if (this.member.geoStateAcronym && !geoStateOptions.find(({ optValue }) => optValue.includes(this.member.geoStateAcronym))) {
        return false;
      }
      return true;
    };

    this.member.spec = {
      firstName: {
        readOnly: !!this.member.firstName,
      },
      lastName: {
        readOnly: !!this.member.lastName,
      },
      email: {
        readOnly: !!this.member.email,
      },
      birthdate: {
        readOnly: !!this.member.birthdate,
      },
      geoStateAcronym: {
        readOnly: enableGeoState(),
      },
      zipCode: {
        readOnly: !!this.member.zipCode,
      },
    };

    this.guardian = defaultValues.guardian;
    this.coverageType = defaultValues.coverageType;
    this.healthInsurance = defaultValues.healthInsurance;
    this.subscriber = defaultValues.subscriber;
    this.legal = defaultValues.legal;
    this.bloom = defaultValues.bloom;
  }

  resetData() {
    this.serviceLoaded = false;
    this.payerList = [];
    this.setServiceData(mapper.getDefaultValues());
  }

  async getPayersList(clientID) {
    const { data: payersData } = await Vue.$http(clientEndpointsTypes.FETCH_PAYERS, clientID);
    this.payerList = payersData?.list || [];
  }

  async getClientSSO(clientRef) {
    const { data } = await Vue.$http(clientEndpointsTypes.FETCH_CLIENT_SSO, clientRef);

    this.sso = data?.sso || [];

    return this.sso[0];
  }

  async setup(enrolmentUUID) {
    if (this.serviceLoaded) return;

    const { data } = await Vue.$http(memberRegistrationEndpointsTypes.GET_MEMBER_ELIGIBILITY, { enrolmentUUID });

    const eligibityData = data?.data;
    const formData = mapper.parseFromAPI(eligibityData);
    this.setServiceData(formData);

    if (eligibityData?.language) {
      store.commit(i18nStoreTypes.mutations.SET_LANGUAGE, eligibityData?.language);
    }

    this.version = data.version;
    this.canSubmit = data.can_submit;

    this.serviceLoaded = true;
  }

  async start(clientRef, {
    clientLegalLinks,
    language,
    programType,
    serviceType,
    utmParams,
  }) {
    const { eligibility, version } = await store.dispatch(enrolmentStoreTypes.actions.START_ENROLMENT, {
      clientRef,
      payload: mapper.parseToAPI(
        this.getServiceData(),
        this.payerList,
        clientLegalLinks,
        language,
        programType,
        serviceType,
        utmParams,
      ),
    });

    this.version = version;
    return parseSubmitResponse(eligibility);
  }

  async submit(enrolmentUUID, {
    clientLegalLinks,
    language,
    programType,
    serviceType,
    utmParams,
  }) {
    const { data } = await Vue.$http(memberRegistrationEndpointsTypes.SAVE_MEMBER_ELIGIBILITY, { enrolmentUUID }, {
      body: {
        version: this.version,
        data: mapper.parseToAPI(
          this.getServiceData(),
          this.payerList,
          clientLegalLinks,
          language,
          programType,
          serviceType,
          utmParams,
        ),
      },
    });

    this.version = data.version;
    return parseSubmitResponse(data?.data);
  }

  async try(clientRef, {
    clientLegalLinks,
    language,
    programType,
    serviceType,
    utmParams,
  }) {
    const payload = mapper.parseToAPI(
      this.getServiceData(),
      this.payerList,
      clientLegalLinks,
      language,
      programType,
      serviceType,
      utmParams,
    );

    payload.client_reference = clientRef;

    let data;
    await Vue.$http(memberRegistrationEndpointsTypes.TRY_MEMBER_ELIGIBILITY, null, {
      body: payload,
    }).then(response => {
      data = response.data;
    }).catch(() => {
      console.log('error, assuming default behaviour');
      data = {
        show_insurance_page: true,
      };
    });

    return data;
  }
}

export default new EligibilityService();
